import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Select from 'react-select';
import './events.css';

const API_URL = import.meta.env.VITE_API_URL;

/* ===============================================================
   定数定義
   =============================================================== */

/** 開催方法の選択肢 */
const HOW = ['オンライン', '現地', 'ハイブリッド'];

/** カテゴリの選択肢 */
const CAG = ['', '展示会', 'セミナー', '交流会', '研修', 'ワークショップ', 'その他'];

/** ステータスの選択肢 */
const STS = ['下書き', '公開', '終了', 'キャンセル'];

/** 受付方法の選択肢 */
const CIT = ['', 'WEB表示', '印刷'];

/** 公開方法の選択肢 */
const VSB = ['', '全体公開', '全体公開(出欠表示)', '招待制'];

/** 受付時間のデフォルト値 */
const RECEPT_START_DEFAULT = '09:00';
const RECEPT_END_DEFAULT = '17:00';

/** 日本語の曜日配列 */
const JWD = ['日', '月', '火', '水', '木', '金', '土'];

/** シンプルなカラーパレット候補 */
const SIMPLE_COLORS = [
  '#17823b', // 緑（メイン）
  '#003f8e', // 青
  '#c62828', // 赤
  '#f57c00', // オレンジ
  '#888888', // ★ 明るめグレー
];


/* ===============================================================
   ヘルパー関数（日付・時刻フォーマット）
   =============================================================== */

/** 数値を2桁にゼロ埋め */
const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Dateオブジェクトから 'YYYY-MM-DD' 形式の文字列を生成
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた日付文字列
 */
const fmtYMD = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/**
 * Dateオブジェクトから 'HH:MM' 形式の文字列を生成（半角コロン）
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた時刻文字列
 */
const fmtHM = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

/**
 * Dateオブジェクトから 'HH：MM' 形式の文字列を生成（全角コロン、表示用）
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた時刻文字列
 */
const fmtHMJP = (d) => `${pad2(d.getHours())}：${pad2(d.getMinutes())}`;

/**
 * Dateオブジェクトから 'YYYY/MM/DD(曜)' 形式の文字列を生成
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた日付文字列（曜日付き）
 */
const fmtYMDJPw = (d) => `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}(${JWD[d.getDay()]})`;

/* ===============================================================
   ヘルパー関数（日時変換・パース）
   =============================================================== */

/**
 * ISO/文字列から <input type="datetime-local"> 用の 'YYYY-MM-DDTHH:MM' 形式に変換
 * @param {string} v - ISO形式または日時文字列
 * @returns {string} - datetime-local用のフォーマット文字列
 */
const fmtToInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d) ? '' :
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/**
 * 'HH:MM:SS' 形式から 'HH:MM' 形式に変換
 * @param {string} t - 時刻文字列
 * @returns {string} - 短縮された時刻文字列
 */
const toHHMM = (t) => (t ? String(t).slice(0, 5) : '');

/**
 * 日付文字列と時刻文字列からDateオブジェクトを生成
 * @param {string} dStr - 日付文字列 (YYYY-MM-DD)
 * @param {string} tStr - 時刻文字列 (HH:MM または HH:MM:SS)
 * @returns {Date|null} - Dateオブジェクト、無効な場合はnull
 */
const toDateFromParts = (dStr, tStr) => {
  if (!dStr || !tStr) return null;
  const t = tStr.length === 5 ? `${tStr}:00` : tStr;
  const s = `${dStr}T${t}`;
  const d = new Date(s);
  return isNaN(d) ? null : d;
};



/**
 * 'YYYY-MM-DDTHH:MM' 形式の文字列を日付と時刻に分割
 * @param {string} dt - datetime-local形式の文字列
 * @returns {Object} - { date: string, time: string }
 */
const splitDateTime = (dt) => {
  if (!dt) return { date: null, time: null };
  const [date, hm] = dt.split('T');
  if (!date || !hm) return { date: null, time: null };
  const time = hm.length === 5 ? `${hm}:00` : hm; // 秒付与
  return { date, time };
};



/**
 * 'YYYY-MM-DD' 形式からDateオブジェクトを生成（日の始まり/終わり指定可能）
 * @param {string} ymd - 日付文字列
 * @param {boolean} endOfDay - trueなら23:59:59、falseなら00:00:00
 * @returns {Date|null} - Dateオブジェクト
 */
const toDateOnly = (ymd, endOfDay = false) => {
  if (!ymd) return null;
  const d = new Date(`${ymd}T${endOfDay ? '23:59:59' : '00:00:00'}`);
  return isNaN(d) ? null : d;
};

/* ===============================================================
   ヘルパー関数（期間表示・フィルタ）
   =============================================================== */

/**
 * 開始・終了の日付時刻から期間文字列を生成
 * 同日: YYYY/MM/DD(曜)　HH：MM～HH：MM
 * 別日: YYYY/MM/DD(曜)～YYYY/MM/DD(曜)　HH：MM～HH：MM
 * @param {string} sd - 開始日 (YYYY-MM-DD)
 * @param {string} st - 開始時刻 (HH:MM)
 * @param {string} ed - 終了日 (YYYY-MM-DD)
 * @param {string} et - 終了時刻 (HH:MM)
 * @param {string} sISO - 開始日時 (ISO形式、フォールバック用)
 * @param {string} eISO - 終了日時 (ISO形式、フォールバック用)
 * @returns {string} - フォーマットされた期間文字列
 */
const formatPeriodFromParts = (sd, st, ed, et, sISO, eISO) => {
  let s = toDateFromParts(sd, st);
  let e = toDateFromParts(ed, et);
  
  // フォールバック：ISO形式から生成
  if (!s && sISO) {
    const d = new Date(sISO);
    if (!isNaN(d)) s = d;
  }
  if (!e && eISO) {
    const d = new Date(eISO);
    if (!isNaN(d)) e = d;
  }
  
  if (s && e) {
    const sameDay = s.getFullYear() === e.getFullYear() &&
                    s.getMonth() === e.getMonth() &&
                    s.getDate() === e.getDate();
    if (sameDay) return `${fmtYMDJPw(s)}　${fmtHMJP(s)}～${fmtHMJP(e)}`;
    return `${fmtYMDJPw(s)}～${fmtYMDJPw(e)}　${fmtHMJP(s)}～${fmtHMJP(e)}`;
  }
  if (s) return `${fmtYMDJPw(s)}　${fmtHMJP(s)}`;
  if (e) return `${fmtYMDJPw(e)}　${fmtHMJP(e)}`;
  return '−';
};

/**
 * イベント開催範囲とフィルタ範囲の交差判定
 * @param {string} evSd - イベント開始日
 * @param {string} evEd - イベント終了日
 * @param {string} from - フィルタ開始日
 * @param {string} to - フィルタ終了日
 * @returns {boolean} - 重なりがあればtrue
 */
const overlaps = (evSd, evEd, from, to) => {
  const s = toDateOnly(evSd);
  const e = toDateOnly(evEd || evSd, true);
  const f = toDateOnly(from);
  const t = toDateOnly(to, true);
  
  if (!f && !t) return true;
  
  const left = f ?? new Date(-8640000000000000);
  const right = t ?? new Date(8640000000000000);
  
  if (!s && !e) return true;
  
  const evLeft = s ?? new Date(-8640000000000000);
  const evRight = e ?? new Date(8640000000000000);
  
  return evLeft <= right && left <= evRight;
};

/**
 * 同日の指定時刻で 'YYYY-MM-DDTHH:MM' 形式を生成
 * @param {string} baseDateStr - 基準となる日時文字列
 * @param {number} hour - 時
 * @param {number} minute - 分
 * @returns {string} - datetime-local形式の文字列
 */
const sameDayAt = (baseDateStr, hour, minute = 0) => {
  if (!baseDateStr) return '';
  const [datePart] = baseDateStr.split('T');
  return `${datePart}T${pad2(hour)}:${pad2(minute)}`;
};

/* ===============================================================
   メインコンポーネント
   =============================================================== */

const AdminEvents = () => {
  /* ---------------------------------------------------------------
     State定義
     --------------------------------------------------------------- */
  
  // イベント一覧関連
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // フィルター関連
  const [q, setQ] = useState('');                    // タイトル/場所検索クエリ
  const [statusFilter, setStatusFilter] = useState(''); // ステータスフィルタ
  const [fromYmd, setFromYmd] = useState('');        // 期間フィルタ（開始）
  const [toYmd, setToYmd] = useState('');            // 期間フィルタ（終了）

  // フォーム初期値
  const blank = {
    visibility: VSB?.[0] ?? '',
    status: STS?.[0] ?? '下書き',
    how_held: HOW?.[0] ?? '',
    category: CAG?.[0] ?? '',
    checkin_type: CIT?.[0] ?? '',

    event_title: '',
    event_title_sub: '',
    event_desc: '',
    information_notes: '',
    event_location: '',
    event_address: '',
    management_office: '',
    attention_notes: '',
    event_url: '',
    invitation_link: '',

    deadline: '',
    schedules: [],
    capacity: '',

     // ★ 任意項目（最大3件まで）
     // { id, label, type: 'text'|'select', options: string[], displaySlot: ''|'main'|'sub1'|'sub2' }[]
    custom_fields: [], 
    custom_fields_locked: false,
    use_datetime_field: false,
    datetime_field_mode: 'ampm',
    datetime_field_label: '来場予定日時',
    datetime_field_required: false,
  };

  // フォーム関連
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState('');

  // ★ 任意項目設定モーダルの表示/非表示
  const [showCustomFieldModal, setShowCustomFieldModal] = useState(false);

  // フォームのロック状態（イベント未選択時はロック）
  const formLocked = selectedId === null;
  
  // ★ コピー用：一時保存
  const pendingCopyRef = useRef(null);

  /* ---------------------------------------------------------------
     Ref定義（自動入力モード制御用）
     --------------------------------------------------------------- */
  
  // 終了日時の自動入力モード（true: 開始に追随 / false: 手動）
  const endAutoRef = useRef(true);
  
  // 開始日時の自動入力モード（true: 日付変更時に09:00へ正規化）
  const startAutoRef = useRef(true);
  
  // 直前の開始「日付」（日付変更検知用）
  const prevStartDateRef = useRef('');
  

  /* ---------------------------------------------------------------
     API呼び出し関数
     --------------------------------------------------------------- */
  
  /**
   * イベント一覧を取得
   */
  const fetchList = async () => {
    setErr('');
    try {
      const r = await axios.get(`${API_URL}/api/admin/events/`);
      if (r.data?.success) {
        setList(r.data.items || []);
      } else {
        setErr('イベント一覧の取得に失敗しました');
      }
    } catch (e) {
      console.error(e);
      setErr('イベント一覧の取得に失敗しました');
    }
  };

  /**
   * 選択されたイベントの詳細を取得してフォームにセット
   * @param {string|number} id - イベントID
   */
  const loadDetail = async (id) => {
    if (!id || id === 'new') return;
    
    try {
      const r = await axios.get(`${API_URL}/api/admin/events/${id}/`);
      if (r.data?.success) {
        const ev = r.data.item || {}; 
     
        setForm({
          event_title: ev.event_title || '',
          event_desc: ev.event_desc || '',
          event_location: ev.event_location || '',
          capacity: ev.capacity ?? '',
          visibility: ev.visibility || '',
          status: ev.status || '',

          how_held: ev.how_held || '',
          category: ev.category || '',
          event_title_sub: ev.event_title_sub || '',
          information_notes: ev.information_notes || '',
          deadline: fmtToInput(ev.deadline),
          checkin_type: ev.checkin_type || '',
          event_url: ev.event_url || '',
          invitation_link: ev.invitation_link || '',
          event_address: ev.event_address || '',
          management_office: ev.management_office || '',
          attention_notes: ev.attention_notes || '',

           schedules: (ev.schedules || []).map(s => ({
            date:              s.date || '',
            start_time:        toHHMM(s.start_time),
            end_time:          toHHMM(s.end_time),
            recept_start_time: toHHMM(s.recept_start_time || ''),
            recept_end_time:   toHHMM(s.recept_end_time || ''),
          })),
          
           // ★ 任意項目
          custom_fields: (ev.custom_fields || []).filter(cf => !cf.type?.startsWith('datetime')),
          custom_fields_locked: !!ev.custom_fields_locked,
          use_datetime_field: (ev.custom_fields || []).some(cf => cf.type?.startsWith('datetime')),
          datetime_field_mode: (() => {
            const dt = (ev.custom_fields || []).find(cf => cf.type?.startsWith('datetime'));
            if (!dt) return 'ampm';
            return dt.type.replace('datetime_', '') || 'ampm';
          })(),
          datetime_field_label: (() => {
            const dt = (ev.custom_fields || []).find(cf => cf.type?.startsWith('datetime'));
            return dt?.label || '来場予定日時';
          })(),
          datetime_field_required: (() => {
            const dt = (ev.custom_fields || []).find(cf => cf.type?.startsWith('datetime'));
            return !!dt?.required;
          })(),
        });
      }
    } catch (e) {
      console.error(e);
    }
  };


// 任意項目セクションがロック状態かどうか
const isCustomFieldSectionLocked = (f) => {
  if (!f) return false;
  return !!f.custom_fields_locked;
};

// イベントが終了済みかどうか（end_datetime があればそれを優先）
const isEventEnded = (form) => {
  if (!form) return false;
  const schedules = form.schedules || [];
  if (schedules.length === 0) return false;
  const lastDate = schedules[schedules.length - 1].date;
  const lastTime = schedules[schedules.length - 1].end_time;
  if (!lastDate) return false;
  const d = new Date(`${lastDate}T${lastTime || '23:59:59'}`);
  if (isNaN(d)) return false;
  return d.getTime() < Date.now();
};

  // ★ エントリー済みイベントの注意メッセージ
const renderEntryNotice = () => {
  const hasEntry = !!form.custom_fields_locked;   // エントリーあり＝選択項目ロック
  const ended    = isEventEnded(form);            // 終了済みか

  // 新規 or どちらの条件も満たさないなら表示しない
  if (!selectedId || selectedId === 'new' || (!hasEntry && !ended)) {
    return null;
  }

  return (
    <div className="aev-entryNotice">
      {/* 終了済みなら先頭に出す */}
      {ended && (
        <span className="aev-entryNotice-text">
          終了済みのイベントです。
        </span>
      )}

      {/* エントリー済みなら続けて注意文を出す */}
      {hasEntry && (
        <span className="aev-entryNotice-text">
          参加者の回答があるため、選択項目は確認のみ可能です。
        </span>
      )}
    </div>
  );
};




  /**
   * イベントを保存（新規作成または更新）
   */
  const saveEvent = async () => {
    // 未選択時は処理しない
    if (selectedId === null) return;
    
    // バリデーション：タイトル必須
    if (!form.event_title.trim()) {
      return alert('タイトルを入力してください');
    }

    // 日付が1件も入っていない場合は強制的に下書き
    const hasValidSchedule = (form.schedules || []).some(s => s.date);
    if (!hasValidSchedule && form.status !== '下書き') {
      if (!window.confirm('日付が入力されていないため、ステータスを下書きに変更して保存します。よろしいですか？')) {
        return;
      }
    }
    
    setSaving(true);
    try {
     
      const { start_datetime, end_datetime, ...rest } = form;
      
      // APIに送信するペイロードを構築
      const payload = {
        ...form,
        status: hasValidSchedule ? form.status : '下書き', 
        schedules: (form.schedules || []).filter(s => s.date),
        capacity: form.capacity === '' || form.capacity == null ? null : Number(form.capacity),
        custom_fields: [
          ...(form.custom_fields || []),
          ...(form.use_datetime_field ? [{
            id: 'datetime_fixed',
            label: form.datetime_field_label,
            type: `datetime_${form.datetime_field_mode || 'ampm'}`,
            options: [],
            optionColors: [],
            displaySlot: '',
            required: !!form.datetime_field_required,
          }] : []),
        ],
      };

      if (selectedId && selectedId !== 'new') {
        // 既存イベントの更新
        await axios.put(
          `${API_URL}/api/admin/events/update/${selectedId}/`,
          payload
        );
      } else {
        // 新規イベントの作成
        const r = await axios.post(
          `${API_URL}/api/admin/events/create/`,
          payload
        );
        if (r.data?.event_id) {
          fetchList();
          setSelectedId(null);
          setForm(blank);
        }
      }
      
      fetchList();
      alert('保存しました。');
    } catch (e) {
      console.error(e);
      alert('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  /**
   * イベントを削除
   */
  const deleteEvent = async () => {
    if (!selectedId || selectedId === 'new') return;
    if (!window.confirm('このイベントを削除しますか？')) return;
    
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/events/delete/${selectedId}/`);
      
      // 状態をリセット
      setSelectedId(null);
      setForm(blank);
      endAutoRef.current = true;
      
      fetchList();
      alert('削除しました');
    } catch (e) {
      console.error(e);
      alert('削除に失敗しました');
    } finally {
      setDeleting(false);
    }
  };


  /**
   * イベントをコピー
   */
 const handleCopyToNew = () => {
    pendingCopyRef.current = {
      ...form,
      schedules: (form.schedules || []).map(s => ({
        ...s,
        date: '',   // 日付だけクリア、時刻はコピー
      })),
      custom_fields_locked: false,
    };
    setSelectedId('new');
    endAutoRef.current = true;   // ★ 空にするので自動モードに戻す
    startAutoRef.current = true; // ★ 同上
  };


  /* ---------------------------------------------------------------
     イベントハンドラ（フィルター）
     --------------------------------------------------------------- */
  
  /**
   * フィルターをクリア
   */
  const handleClearFilters = () => {
    setQ('');
    setStatusFilter('');
    setFromYmd('');
    setToYmd('');
  };

  /* ---------------------------------------------------------------
     イベントハンドラ（開始日時入力）
     --------------------------------------------------------------- */
  
  /**
   * 開始日時フィールドのフォーカス時処理
   * 自動モード時は09:00に正規化
   */
  const handleStartDatetimeFocus = () => {
    if (!startAutoRef.current) return;

    setForm(f => {
      // 現在の値から日付だけ取り出す or 今日の日付
      const cur = f.start_datetime || '';
      let datePart = cur.split('T')[0];
      if (!datePart) {
        const d = new Date();
        datePart = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
      }
      const fixedStart = `${datePart}T09:00`;

      // 直前の日付を更新
      prevStartDateRef.current = datePart;

      // 終了は自動モードなら同日17:00へ追随
      const next = { ...f, start_datetime: fixedStart };
      if (endAutoRef.current) {
        next.end_datetime = sameDayAt(fixedStart, 17, 0);
      }
      return next;
    });
  };

  /**
   * 開始日時フィールドの変更時処理
   * @param {Event} e - 変更イベント
   */
  const handleStartDatetimeChange = (e) => {
    const v = e.target.value;
    const [datePart, hm = ''] = (v || '').split('T');
    const timePart = hm.slice(0, 5);
    const prevDate = prevStartDateRef.current;
    const dateChanged = datePart && prevDate && datePart !== prevDate;

    setForm(f => {
      // 空の場合はリセット
      if (!v) {
        startAutoRef.current = true;
        endAutoRef.current = true;
        prevStartDateRef.current = '';
        return { ...f, start_datetime: '', end_datetime: '' };
      }

      let nextStart = v;

      // 日付が変わったら09:00に固定（自動モード時）
      if (startAutoRef.current) {
        if (!prevDate || dateChanged) {
          if (datePart) nextStart = `${datePart}T09:00`;
        } else if (timePart && timePart !== '09:00') {
          // 同日内でユーザーが時刻を触ったら以降は手動モード
          startAutoRef.current = false;
        }
      }

      if (datePart) prevStartDateRef.current = datePart;

      const next = { ...f, start_datetime: nextStart };
      if (endAutoRef.current) {
        next.end_datetime = nextStart ? sameDayAt(nextStart, 17, 0) : '';
      }
      return next;
    });
  };

  /**
   * 開始日時フィールドのブラー時処理
   * ブラウザが現在時刻に戻すことがある対策
   */
  const handleStartDatetimeBlur = () => {
    if (!startAutoRef.current) return;
    
    setTimeout(() => {
      setForm(f => {
        const v = f.start_datetime;
        if (!v) return f;
        const [datePart] = v.split('T');
        if (!datePart) return f;
        const hhmm = v.slice(-5);
        if (hhmm === '09:00') return f;

        const fixedStart = `${datePart}T09:00`;
        const next = { ...f, start_datetime: fixedStart };
        if (endAutoRef.current) {
          next.end_datetime = sameDayAt(fixedStart, 17, 0);
        }
        return next;
      });
    }, 0);
  };

  /* ---------------------------------------------------------------
     イベントハンドラ（終了日時入力）
     --------------------------------------------------------------- */
  
  /**
   * 終了日時フィールドの変更時処理
   * ユーザーが触った瞬間から手動モードに切り替え
   * @param {Event} e - 変更イベント
   */
  const handleEndDatetimeChange = (e) => {
    endAutoRef.current = false;
    setForm(f => ({ ...f, end_datetime: e.target.value }));
  };

  /* ---------------------------------------------------------------
     イベントハンドラ（受付時間入力）
     --------------------------------------------------------------- */
  
// 受付開始時間のフォーカス時処理
// 空の場合はデフォルト値をそのままセットしておく
const handleReceptStartFocus = (e) => {
  if (!form.recept_start_time) {
    setForm(f => ({ ...f, recept_start_time: RECEPT_START_DEFAULT }));
    if (e.target.showPicker) {
      queueMicrotask(() => e.target.showPicker());
    }
  }
};

// 受付開始時間の変更時処理
const handleReceptStartChange = (e) => {
  setForm(f => ({ ...f, recept_start_time: e.target.value }));
};

 

  /**
   * 受付終了時間のフォーカス時処理
   * 空の場合はデフォルト値を一時的にセット
   */
  const handleReceptEndFocus = (e) => {
  if (!form.recept_end_time) {
    setForm(f => ({ ...f, recept_end_time: RECEPT_END_DEFAULT }));
    if (e.target.showPicker) {
      queueMicrotask(() => e.target.showPicker());
    }
  }
};

  /**
   * 受付終了時間の変更時処理
   */
  const handleReceptEndChange = (e) => {
  setForm(f => ({ ...f, recept_end_time: e.target.value }));
};



  /* ---------------------------------------------------------------
     useMemo（計算結果のメモ化）
     --------------------------------------------------------------- */
  
  /**
   * フィルタリングされたイベント一覧
   * ステータス、期間、キーワードでフィルタリング
   */
  const filteredList = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (list || []).filter(ev => {
      // ステータスフィルタ
      if (statusFilter && ev.status !== statusFilter) return false;
      
      // 期間フィルタ
      if (!overlaps(ev.start_date, ev.end_date, fromYmd, toYmd)) return false;
      
      // キーワードフィルタ
      if (qq) {
        const hay = `${ev.event_title || ''} ${ev.event_location || ''} ${ev.visibility || ''} ${ev.status || ''}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      return true;
    });
  }, [list, statusFilter, fromYmd, toYmd, q]);

  /**
   * react-select用のオプションリスト
   * 新規作成オプション + イベント一覧
   */
  const options = useMemo(() => {
    const evOpts = filteredList.map(ev => {
      const period = formatPeriodFromParts(
        ev.start_date, ev.start_time,
        ev.end_date, ev.end_time,
        ev.start_time_iso, ev.end_time_iso
      );
      const searchableText = `${ev.event_title || ''} ${ev.event_location || ''} ${ev.visibility || ''} ${ev.status || ''} ${period || ''}`.toLowerCase();
      
      return {
        value: ev.event_id,
        label: (
          <div className="sel-option">
            <div className="sel-line1">{ev.event_title}</div>
            <div className="sel-line2">
              <span className="aev-badge">{ev.visibility || '-'}</span>
              <span className="aev-badge">{ev.status || '-'}</span>
              <span className="sel-period">{period}</span>
            </div>
          </div>
        ),
        __search: searchableText,
      };
    });
    
    return [
      { value: 'new', label: <div className="sel-option"><b>＋ 新規作成</b></div>, __search: 'new' },
      ...evOpts
    ];
  }, [filteredList]);

  /**
   * 現在選択されているオプション
   */
  const currentValue = options.find(o => String(o.value) === String(selectedId)) || null;

   /* ---------------------------------------------------------------
     イベントハンドラ（任意項目）
     --------------------------------------------------------------- */

  const openCustomFieldModal = () => {
    setShowCustomFieldModal(true);
  };

  const closeCustomFieldModal = () => {
    setShowCustomFieldModal(false);
  };

  // 項目追加（最大3件まで）
const handleAddCustomField = () => {
  setForm(f => {
    if (isCustomFieldSectionLocked(f)) {
      return f;
    }

    const current = f.custom_fields || [];
    if (current.length >= 5) {
      alert('追加できる任意項目は最大5件までです。');
      return f;
    }

    const next = [
      ...current,
      {
        id: Date.now(),
        label: '',
        type: 'text',
        options: [''],
        // ▼ 追加
        optionColors: ['#17823b'], // 各プルダウン選択肢の色
        displaySlot: '',
        required: false,
        badgeColor: '#17823b',     // メイン表示用バッジ色
      },
    ];
    return { ...f, custom_fields: next };
  });
};

const SimpleColorPalette = ({ value, onChange, disabled }) => {
  return (
    <div className="aev-colorPalette">
      {SIMPLE_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          className={
            'aev-colorChip' + (value === color ? ' aev-colorChip--active' : '')
          }
          style={{ backgroundColor: color }}
          onClick={() => {
            if (!disabled) {
              onChange(color);
            }
          }}
          disabled={disabled}
        />
      ))}
    </div>
  );
};


// 項目の基本情報更新
const handleChangeCustomField = (index, key, value) => {
  setForm(f => {
    if (isCustomFieldSectionLocked(f)) {
      return f;
    }

    const list = [...(f.custom_fields || [])];
    const current = list[index];
    if (!current) {
      return f;
    }

    const updated = [...list];
    const target = { ...current };

    if (key === 'type') {
      target.type = value;
      if (value === 'select' && (!target.options || target.options.length === 0)) {
        target.options = [''];
        target.optionColors = ['#17823b'];
      }
    } else if (key === 'displaySlot') {
      const newSlot = value || '';

      if (newSlot) {
        updated.forEach((cf, i) => {
          if (i !== index && cf && cf.displaySlot === newSlot) {
            updated[i] = { ...cf, displaySlot: '' };
          }
        });
      }

      target.displaySlot = newSlot;
    } else if (key === 'label') {
      target.label = value;
    } else if (key === 'required') {
      target.required = !!value;
    } else if (key === 'badgeColor') {
      target.badgeColor = value || '#17823b';
    }

    updated[index] = target;
    return { ...f, custom_fields: updated };
  });
};



// セレクト用の選択肢を追加
const handleAddOption = (index) => {
  setForm(f => {
    if (isCustomFieldSectionLocked(f)) {
      return f;
    }

    const list = [...(f.custom_fields || [])];
    const current = list[index];
    if (!current) {
      return f;
    }

    const target = { ...current };
    const opts = [...(target.options || [])];
    const cols = [...(target.optionColors || [])];

    opts.push('');
    cols.push('#17823b');   // 新しい選択肢のデフォルト色

    target.options = opts;
    target.optionColors = cols;
    list[index] = target;

    return { ...f, custom_fields: list };
  });
};

// セレクト用の選択肢ラベル変更
const handleChangeOption = (index, optIndex, value) => {
  setForm(f => {
    if (isCustomFieldSectionLocked(f)) {
      return f;
    }

    const list = [...(f.custom_fields || [])];
    const current = list[index];
    if (!current) {
      return f;
    }

    const target = { ...current };
    const opts = [...(target.options || [])];
    opts[optIndex] = value;
    target.options = opts;
    list[index] = target;

    return { ...f, custom_fields: list };
  });
};

// セレクト用の選択肢色変更（新規追加）
const handleChangeOptionColor = (index, optIndex, value) => {
  setForm(f => {
    if (isCustomFieldSectionLocked(f)) {
      return f;
    }

    const list = [...(f.custom_fields || [])];
    const current = list[index];
    if (!current) {
      return f;
    }

    const target = { ...current };
    const cols = [...(target.optionColors || [])];

    // options 長に合わせて不足分を埋めておく
    while (cols.length < (target.options || []).length) {
      cols.push('#17823b');
    }

    cols[optIndex] = value || '#17823b';
    target.optionColors = cols;
    list[index] = target;

    return { ...f, custom_fields: list };
  });
};

// セレクト用の選択肢削除
const handleRemoveOption = (index, optIndex) => {
  setForm(f => {
    if (isCustomFieldSectionLocked(f)) {
      return f;
    }

    const list = [...(f.custom_fields || [])];
    const current = list[index];
    if (!current) {
      return f;
    }

    const target = { ...current };
    const opts = [...(target.options || [])];
    const cols = [...(target.optionColors || [])];

    opts.splice(optIndex, 1);
    cols.splice(optIndex, 1);

    target.options = opts;
    target.optionColors = cols;
    list[index] = target;

    return { ...f, custom_fields: list };
  });
};

// 任意項目を削除
const handleRemoveCustomField = (index) => {
  setForm(f => {
    if (isCustomFieldSectionLocked(f)) {
      return f;
    }

    const current = f.custom_fields || [];
    const next = [...current];
    next.splice(index, 1); // index 番目を削除

    return { ...f, custom_fields: next };
  });
};


// value を最大 maxLines 行に制限する
const limitLines = (value, maxLines) => {
  const lines = String(value || '').split(/\r?\n/);
  return lines.slice(0, maxLines).join('\n');
};

// onChange 用：行数を制限してから setForm する
const handleLimitedChange = (field, maxLines) => (e) => {
  const limited = limitLines(e.target.value, maxLines);
  setForm(prev => ({
    ...prev,
    [field]: limited,
  }));
};

// onKeyDown 用：すでに maxLines 行ある場合は Enter を無効化
const handleLimitedKeyDown = (value, maxLines) => (e) => {
  if (e.key === 'Enter') {
    const lines = String(value || '').split(/\r?\n/);
    if (lines.length >= maxLines) {
      e.preventDefault();
    }
  }
};


  /* ---------------------------------------------------------------
     useEffect（副作用処理）
     --------------------------------------------------------------- */
  
  // 初回ロード時にイベント一覧を取得
  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 選択イベント変更時に詳細を取得またはフォームをリセット
  useEffect(() => {
    if (selectedId && selectedId !== 'new') {
      // 既存イベントの詳細を取得
      (async () => {
        await loadDetail(selectedId);
        // 既存イベント：手動モードに設定
        requestAnimationFrame(() => {
          endAutoRef.current = false;
          startAutoRef.current = false;
          prevStartDateRef.current = (form.start_datetime || '').split('T')[0] || '';
        });
      })();
    } else {
      // 新規：フォームをリセットして自動モードに設定
      if (pendingCopyRef.current) {
        // コピーデータがあればそれをセット
        setForm(pendingCopyRef.current);
        pendingCopyRef.current = null;
        endAutoRef.current = false;
        startAutoRef.current = false;
      } else {
        // 通常の新規作成
        setForm(blank);
        endAutoRef.current = true;
        startAutoRef.current = true;
        prevStartDateRef.current = '';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // フィルタリング後に選択中のイベントが存在しなくなった場合の処理
  useEffect(() => {
    if (selectedId === 'new') return;
    const exists = filteredList.some(ev => String(ev.event_id) === String(selectedId));
    if (!exists) {
      setSelectedId(null);
      setForm(blank);
      endAutoRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredList, selectedId]);

  /* ---------------------------------------------------------------
     レンダリング関数
     --------------------------------------------------------------- */
  
  /**
   * 絞り込みUIを表示
   */
  const renderFilterSection = () => (
    <div className="aev-listOps" style={{ flexWrap: 'wrap', gap: 8 }}>
      <div className="listTitle">イベント管理</div>
      
      {/* タイトル/場所検索 */}
      <input
        className="aev-searchInput"
        placeholder="タイトル/場所で絞り込み"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ minWidth: 240 }}
      />
      
      {/* ステータスフィルタ */}
      <select
        className="aev-searchInput"
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        style={{ width: 140 }}
      >
        <option value="">状態: すべて</option>
        {STS.map(v => <option key={v} value={v}>{v}</option>)}
      </select>
      
      {/* 期間フィルタ（開始） */}
      <input
        type="date"
        className="aev-searchInput"
        value={fromYmd}
        onChange={(e) => setFromYmd(e.target.value)}
        title="期間From"
      />
      
      {/* 期間フィルタ（終了） */}
      <input
        type="date"
        className="aev-searchInput"
        value={toYmd}
        onChange={(e) => setToYmd(e.target.value)}
        title="期間To"
      />
      
      {/* クリアボタン */}
      <button className="aev-btn" onClick={handleClearFilters}>
        クリア
      </button>
    </div>
  );

  /**
   * イベント選択プルダウンを表示
   */
  const renderEventSelector = () => (
    <div style={{ marginTop: 8, marginBottom: 12 }}>
      <Select
        options={options}
        value={currentValue}
        onChange={(opt) => setSelectedId(opt?.value || null)}
        isSearchable
        placeholder="選択してください"
        classNamePrefix="sel"
        filterOption={(candidate, input) => {
          if (!input) return true;
          const hay = (candidate.data.__search || '').toLowerCase();
          return hay.includes(input.toLowerCase());
        }}
        styles={{
          option: (base) => ({ ...base, padding: '8px 10px' }),
          singleValue: (base) => ({ ...base, display: 'flex', alignItems: 'center', gap: 8 }),
          menu: (base) => ({ ...base, zIndex: 20 }),
        }}
      />
    </div>
  );

  /**
   * フォームの基本設定セクションを表示
   */
  const renderBasicSettingsFields = () => (
    <>
      <label>公開方法
        <select value={form.visibility} onChange={e => setForm({ ...form, visibility: e.target.value })}>
          {VSB.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </label>
      <label>状態
        <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
          {STS.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </label>
      <label>開催方法
        <select value={form.how_held} onChange={e => setForm({ ...form, how_held: e.target.value })}>
          {HOW.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </label>
      <label>種別
        <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
          {CAG.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </label>
    </>
  );

  /**
   * フォームのタイトル・説明セクションを表示
   */
  const renderTitleDescriptionFields = () => (
    <>
      <label>タイトル
        <input
          type="text"
          value={form.event_title}
          onChange={e => setForm({ ...form, event_title: e.target.value })}
        />
      </label>
      <label>サブタイトル
        <input
          type="text"
          value={form.event_title_sub}
          onChange={e => setForm({ ...form, event_title_sub: e.target.value })}
        />
      </label>
      <label className="aev-span2">説明
        <textarea
          rows={3}
          value={form.event_desc}
          onChange={e => setForm({ ...form, event_desc: e.target.value })}
        />
      </label>
    </>
  );

  /**
   * フォームの日時セクションを表示
   */
  const renderDateTimeFields = () => (
  <>
    <div className="aev-span2">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span>日程</span>
        <button type="button" className="aev-btn" onClick={() =>
          setForm(f => {
            const prev = f.schedules?.[f.schedules.length - 1];
            return {
              ...f,
              schedules: [...(f.schedules || []), {
                date:              prev?.date || '',
                start_time:        prev?.start_time        || '09:00',
                end_time:          prev?.end_time          || '17:00',
                recept_start_time: prev?.recept_start_time || '',
                recept_end_time:   prev?.recept_end_time   || '',
              }]
            };
          })
        }>＋ 日程追加</button>
      </div>

      {(form.schedules || []).map((sc, i) => (
        <div key={i} className="aev-scheduleRow">

          <span className="aev-scheduleRow-label">開催日</span>
          <input type="date" value={sc.date}
            onChange={e => {
              const next = [...form.schedules];
              next[i] = { ...next[i], date: e.target.value };
              setForm(f => ({ ...f, schedules: next }));
            }}
          />

          <div className="aev-scheduleRow-group">
            <span className="aev-scheduleRow-label">開催時間</span>
            <input type="time" value={sc.start_time}
              onChange={e => {
                const next = [...form.schedules];
                next[i] = { ...next[i], start_time: e.target.value };
                setForm(f => ({ ...f, schedules: next }));
              }}
            />
            <span className="aev-scheduleRow-sep">〜</span>
            <input type="time" value={sc.end_time}
              onChange={e => {
                const next = [...form.schedules];
                next[i] = { ...next[i], end_time: e.target.value };
                setForm(f => ({ ...f, schedules: next }));
              }}
            />
          </div>

          <div className="aev-scheduleRow-group">
            <span className="aev-scheduleRow-label">受付時間</span>
            <input type="time" value={sc.recept_start_time || ''}
              onChange={e => {
                const next = [...form.schedules];
                next[i] = { ...next[i], recept_start_time: e.target.value };
                setForm(f => ({ ...f, schedules: next }));
              }}
            />
            <span className="aev-scheduleRow-sep">〜</span>
            <input type="time" value={sc.recept_end_time || ''}
              onChange={e => {
                const next = [...form.schedules];
                next[i] = { ...next[i], recept_end_time: e.target.value };
                setForm(f => ({ ...f, schedules: next }));
              }}
            />
          </div>

          <button type="button" className="aev-btn danger"
            onClick={() => setForm(f => ({
              ...f,
              schedules: f.schedules.filter((_, idx) => idx !== i)
            }))}
          >削除</button>

        </div>
      ))}
    </div>

    <label>募集参加締切
      <input type="datetime-local" value={form.deadline}
        onChange={e => setForm({ ...form, deadline: e.target.value })}
      />
    </label>
    <label>受付方法
      <select value={form.checkin_type}
        onChange={e => setForm({ ...form, checkin_type: e.target.value })}>
        {CIT.map(v => <option key={v} value={v}>{v}</option>)}
      </select>
    </label>
  </>
);

  /**
   * フォームのURL・リンクセクションを表示
   */
  const renderUrlFields = () => (
    <>
      <label>イベントURL
        <input
          type="text"
          value={form.event_url}
          onChange={e => setForm({ ...form, event_url: e.target.value })}
        />
      </label>
      <label>招待リンク
        <input
          type="text"
          value={form.invitation_link}
          onChange={e => setForm({ ...form, invitation_link: e.target.value })}
        />
      </label>
    </>
  );

  /**
   * フォームの会場・定員セクションを表示
   */
  const renderLocationCapacityFields = () => (
    <>
      <label>会場
        <input
          type="text"
          value={form.event_location}
          onChange={e => setForm({ ...form, event_location: e.target.value })}
        />
      </label>
      <label>定員
        <input
          type="number"
          inputMode="numeric"
          value={form.capacity}
          onChange={e => setForm({ ...form, capacity: e.target.value })}
        />
      </label>
    </>
  );

  /**
   * フォームの住所・運営事務局セクションを表示
   */
  const renderAddressOfficeFields = () => (
  <>
    <label className="aev-span3">住所
      <textarea
        rows={2}
        style={{ resize: 'none' }}  // ← これ以上広げられなくする（不要なら消してOK）
        value={form.event_address}
        onKeyDown={handleLimitedKeyDown(form.event_address, 2)}
        onChange={handleLimitedChange('event_address', 2)}
        placeholder={`〒000-0000\n東京都〇〇区〇〇 1-2-3`}
        
      />
    </label>

    <label className="aev-span3">運営事務局
      <textarea
        rows={3}
        style={{ resize: 'none' }}  // ← 同じくリサイズ禁止
        value={form.management_office}
        onKeyDown={handleLimitedKeyDown(form.management_office, 3)}
        onChange={handleLimitedChange('management_office', 3)}
        placeholder={`〇〇〇事務局\n〒000-0000\n東京都〇〇区〇〇 1-2-3`}
      />
    </label>
  </>
);


  /**
   * フォームのご案内・注意事項セクションを表示
   */
  const renderNotesFields = () => (
    <label className="aev-span2">ご案内・注意事項
      <textarea
        rows={6}
        value={form.information_notes}
        onChange={e => setForm({ ...form, information_notes: e.target.value })}
      />
    </label>
  );

  /**
   * 入力フォーム全体を表示
   */
  const renderForm = () => (
     <div className="aev-formScroll">
    <fieldset
      className="aev-formGrid"
      disabled={formLocked}
      aria-disabled={formLocked}
    >
      {renderBasicSettingsFields()}
      {renderTitleDescriptionFields()}
      {renderDateTimeFields()}
      {renderUrlFields()}
      {renderLocationCapacityFields()}
      {renderAddressOfficeFields()}
      {renderNotesFields()}
    </fieldset>
  </div>
  );
   

const renderCustomFieldModal = () => {
  if (!showCustomFieldModal) return null;

  const fields = form.custom_fields || [];
  const usedSlots = new Set(fields.map(cf => cf.displaySlot).filter(v => v));
  const sectionLocked = isCustomFieldSectionLocked(form);

  return (
    <div className="aev-modalBackdrop">
      <div className="aev-modal">
        {/* ヘッダ */}
        <div className="aev-modalHeader">
          <h3>任意選択項目の設定</h3>
          {sectionLocked && (
            <span className="aev-modalHeader-lockNotice">
              ※参加者の回答があるため、任意項目は編集できません
            </span>
          )}
        </div>

        {/* スクロール領域 */}
        <div className="aev-modalScroll">

          {/* ★ 日時入力欄（固定・常に最上部） */}
          <div className="aev-cfItem aev-cfDatetimeBlock">
            <div className="aev-cfDatetimeBlock-row">
              <label className="aev-cfItem-required aev-cfDatetimeBlock-label">
                <input
                  type="checkbox"
                  checked={!!form.use_datetime_field}
                  onChange={e => setForm(f => ({ ...f, use_datetime_field: e.target.checked }))}
                  disabled={sectionLocked}
                />
                <span style={{ fontWeight: 600 }}>参加者に日時を選択してもらう</span>
              </label>

              {form.use_datetime_field && (
                <select
                  value={form.datetime_field_mode || 'ampm'}
                  onChange={e => setForm(f => ({ ...f, datetime_field_mode: e.target.value }))}
                  disabled={sectionLocked}
                  className="aev-cfDatetimeBlock-select"
                >
                  <option value="ampm">午前 / 午後</option>
                  <option value="30min">30分間隔</option>
                  <option value="60min">1時間間隔</option>
                </select>
              )}
            </div>

            {/* ★ チェックON時だけラベル・必須を表示 */}
            {form.use_datetime_field && (
              <div className="aev-cfItem-meta" style={{ marginTop: 10 }}>
                <div className="aev-cfField" style={{ flex: 1 }}>
                  <span className="aev-cfField-label">項目名</span>
                  <input
                    type="text"
                    value={form.datetime_field_label ?? '来場予定日時'}
                    onChange={e => setForm(f => ({ ...f, datetime_field_label: e.target.value }))}
                    disabled={sectionLocked}
                    style={{ height: 32, padding: '0 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14, width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                <label className="aev-cfItem-required" style={{ marginLeft: 16, whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={!!form.datetime_field_required}
                    onChange={e => setForm(f => ({ ...f, datetime_field_required: e.target.checked }))}
                    disabled={sectionLocked}
                  />
                  <span>必須</span>
                </label>
              </div>
            )}
          </div>

          <div className="aev-cfModalContent">
            {fields.length === 0 && (
              <p className="aev-cfModalEmpty">
                まだ任意項目はありません。「項目を追加」から作成してください。
              </p>
            )}

            {fields.map((cf, index) => {
              const disabled = sectionLocked;
              const optionColors = cf.optionColors || [];

              return (
                <div key={cf.id || index} className="aev-cfItem">
                  {/* 削除ボタン */}
                  <button
                    type="button"
                    className="aev-btn danger aev-cfItem-deleteBtn"
                    onClick={() => handleRemoveCustomField(index)}
                    disabled={disabled}
                  >
                    削除
                  </button>

                  {/* 項目名・種別・ディスプレイ */}
                  <div className="aev-cfItem-grid">
                    <div className="aev-cfField">
                      <span className="aev-cfField-label">項目名</span>
                      <input
                        type="text"
                        value={cf.label || ''}
                        onChange={e => handleChangeCustomField(index, 'label', e.target.value)}
                        disabled={disabled}
                      />
                    </div>

                    <div className="aev-cfField">
                      <span className="aev-cfField-label">種別</span>
                      <select
                        value={cf.type || 'text'}
                        onChange={e => handleChangeCustomField(index, 'type', e.target.value)}
                        disabled={disabled}
                      >
                        <option value="text">テキストボックス</option>
                        <option value="select">プルダウン</option>
                      </select>
                    </div>

                    <div className="aev-cfField">
                      <span className="aev-cfField-label">ディスプレイ</span>
                      <select
                        value={cf.displaySlot || ''}
                        onChange={e => handleChangeCustomField(index, 'displaySlot', e.target.value)}
                        disabled={disabled}
                      >
                        <option value="">未設定</option>
                        {(!usedSlots.has('main') || cf.displaySlot === 'main') && (
                          <option value="main">メイン</option>
                        )}
                        {(!usedSlots.has('sub1') || cf.displaySlot === 'sub1') && (
                          <option value="sub1">サブ1(13文字まで)</option>
                        )}
                        {(!usedSlots.has('sub2') || cf.displaySlot === 'sub2') && (
                          <option value="sub2">サブ2(13文字まで)</option>
                        )}
                        {(!usedSlots.has('sub3') || cf.displaySlot === 'sub3') && (
                          <option value="sub3">サブ3</option>
                        )}
                        {(!usedSlots.has('sub4') || cf.displaySlot === 'sub4') && (
                          <option value="sub4">サブ4</option>
                        )}
                      </select>
                    </div>
                  </div>

                  {/* 必須チェック＋バッジ色 */}
                  <div className="aev-cfItem-meta">
                    <label className="aev-cfItem-required">
                      <input
                        type="checkbox"
                        checked={!!cf.required}
                        onChange={e => handleChangeCustomField(index, 'required', e.target.checked)}
                        disabled={disabled}
                      />
                      <span>必須</span>
                    </label>

                    {cf.type === 'text' && cf.displaySlot === 'main' && (
                        <div className="aev-cfItem-badgeColor">
                          <span>バッジ色</span>
                          <SimpleColorPalette
                            value={cf.badgeColor || SIMPLE_COLORS[0]}
                            onChange={(color) => handleChangeCustomField(index, 'badgeColor', color)}
                            disabled={disabled}
                          />
                        </div>
                      )}
                  </div>

                  {/* プルダウンの選択肢 */}
                 {cf.type === 'select' && (
                    <div className="aev-cfItem-options">
                      <div className="aev-cfItem-options-title">プルダウンの選択肢</div>

                      {(cf.options || []).map((opt, optIndex) => {
                        const optionColor = optionColors[optIndex] || SIMPLE_COLORS[0];

                        return (
                          <div key={optIndex} className="aev-cfItem-optionRow">
                            <input
                              type="text"
                              value={opt}
                              onChange={e => handleChangeOption(index, optIndex, e.target.value)}
                              disabled={disabled}
                            />
                            {cf.displaySlot === 'main' && (
                              <SimpleColorPalette
                                value={optionColor}
                                onChange={(color) =>
                                  handleChangeOptionColor(index, optIndex, color)
                                }
                                disabled={disabled}
                              />
                            )}
                            <button
                              type="button"
                              className="aev-btn"
                              onClick={() => handleRemoveOption(index, optIndex)}
                              disabled={disabled}
                            >
                              −
                            </button>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        className="aev-btn aev-cfItem-addOptionBtn"
                        onClick={() => handleAddOption(index)}
                        disabled={disabled}
                      >
                        ＋ 選択肢追加
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* フッター */}
        <div className="aev-modalFooter">
          <button
            type="button"
            className="aev-btn"
            onClick={handleAddCustomField}
            disabled={sectionLocked || fields.length >= 5}
          >
            項目を追加（最大5件）
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="aev-btn"
            onClick={closeCustomFieldModal}
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};



  /**
   * 操作ボタンを表示
   */
  const renderActionButtons = () => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>

      {/* ★ 左寄せ・他と分離 */}
      {selectedId && selectedId !== 'new' && (
        <button
          className="aev-btn"
          onClick={handleCopyToNew}
          style={{ marginRight: 'auto' }}  // ← これだけで左端に押し出される
        >
          コピーして新規作成
        </button>
      )}

      {/* 以下は既存のまま（右寄り） */}
      {selectedId && selectedId !== 'new' && (
        <button className="aev-btn danger" onClick={deleteEvent} disabled={deleting}>
          {deleting ? '削除中…' : '削除'}
        </button>
      )}
      <button
        className="aev-btn"
        onClick={openCustomFieldModal}
        disabled={selectedId === null}
      >
        選択項目追加
      </button>
      <button
        className="aev-btn primary"
        onClick={saveEvent}
        disabled={saving || selectedId === null}
      >
        {saving ? '保存中…' : '保存'}
      </button>
    </div>
  );

  /**
   * エラーメッセージを表示
   */
  const renderError = () => (
    err && <div className="aev-error" style={{ marginTop: 8 }}>{err}</div>
  );

  /* ---------------------------------------------------------------
     メインレンダリング
     --------------------------------------------------------------- */
  return (
    <div className="aev-page" style={{ gridTemplateAreas: '"hd" "dt"', gridTemplateColumns: '1fr' }}>
      <div className="aev-detailCard" style={{ gridArea: 'dt' }}>
        {/* 絞り込みUI */}
        {renderFilterSection()}
        
        {/* イベント選択プルダウン */}
        {renderEventSelector()}

        {/* ★ エントリー済みイベント注意メッセージ */}
        {renderEntryNotice()}
        
        {/* 入力フォーム */}
        {renderForm()}
        
        {/* 操作ボタン */}
        {renderActionButtons()}
        
        {/* エラー表示 */}
        {renderError()}
      </div>

       {/* ★ 任意項目設定モーダル */}
      {renderCustomFieldModal()}

    </div>
  );
};

export default AdminEvents;